import {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { CatalogApi } from '@backstage/catalog-client';
import { useApi, toastApiRef, ToastApi } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  providersApiRef,
  ProvidersApi,
} from '@internal/backstage-plugin-catalog-devops-react';
import {
  Group,
  GroupEntity,
} from '@internal/backstage-plugin-catalog-devops-common';
import { AdminApi, adminApiRef } from '../apis';
import { ActionTypes } from './types';

interface GroupsContextProviderProps {
  children: ReactNode;
}

type LoadCompletedGroupsAction = {
  type: ActionTypes.LOAD_COMPLETED;
  payload: GroupEntity[];
};

type DeleteGroupAction = {
  type: ActionTypes.DELETE;
};

type DeleteCompletedGroupAction = {
  type: ActionTypes.DELETE_COMPLETED;
};

type CreateCompletedGroupAction = {
  type: ActionTypes.CREATE_COMPLETED;
};

type EditCompletedGroupAction = {
  type: ActionTypes.EDIT_COMPLETED;
};
type CreateGroupAction = {
  type: ActionTypes.CREATE;
  payload: Group;
};

type EditGroupAction = {
  type: ActionTypes.EDIT;
  payload: Group;
};

type SelectGroupAction = {
  type: ActionTypes.SELECT;
  payload: GroupEntity;
};

type UnselectGroupAction = {
  type: ActionTypes.UNSELECT;
};

type GroupsAction =
  | LoadCompletedGroupsAction
  | DeleteGroupAction
  | CreateGroupAction
  | EditGroupAction
  | SelectGroupAction
  | UnselectGroupAction
  | DeleteCompletedGroupAction
  | CreateCompletedGroupAction
  | EditCompletedGroupAction;

type GroupsState = {
  groups: GroupEntity[];
  selectedGroup: GroupEntity | undefined;
  status: string;
  newGroup: Group | undefined;
};

type GroupsContextType = {
  groups: GroupEntity[];
  selectedGroup: GroupEntity | undefined;
  isLoading: boolean;
  deleteGroup(): void;
  createGroup(group: Group): void;
  editGroup(group: Group): void;
  selectGroup(group: GroupEntity): void;
  unselectGroup(): void;
};

const initialGroupsState = {
  groups: [],
  selectedGroup: undefined,
  status: 'initial_load',
  newGroup: undefined,
};

export const GroupsContext = createContext<GroupsContextType | null>(null);

export function GroupsContextProvider(
  props: Readonly<GroupsContextProviderProps>,
) {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const adminApi = useApi<AdminApi>(adminApiRef);
  const providersApi = useApi<ProvidersApi>(providersApiRef);
  const toastApi = useApi<ToastApi>(toastApiRef);

  const groupsReducer = (
    state: GroupsState,
    action: GroupsAction,
  ): GroupsState => {
    switch (action.type) {
      case ActionTypes.LOAD_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.groups = action.payload;
        stateCopy.status = 'wait';
        return stateCopy;
      }
      case ActionTypes.DELETE: {
        const stateCopy = { ...state };
        stateCopy.status = 'delete';
        return stateCopy;
      }
      case ActionTypes.DELETE_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.selectedGroup = undefined;
        stateCopy.status = 'load';
        return stateCopy;
      }
      case ActionTypes.CREATE: {
        const stateCopy = { ...state };
        stateCopy.status = 'create';
        stateCopy.newGroup = action.payload;
        return stateCopy;
      }
      case ActionTypes.CREATE_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.status = 'load';
        stateCopy.newGroup = undefined;
        return stateCopy;
      }
      case ActionTypes.EDIT: {
        const stateCopy = { ...state };
        stateCopy.newGroup = action.payload;
        stateCopy.status = 'edit';
        return stateCopy;
      }
      case ActionTypes.EDIT_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.newGroup = undefined;
        stateCopy.selectedGroup = undefined;
        stateCopy.status = 'load';
        return stateCopy;
      }
      case ActionTypes.SELECT: {
        const stateCopy = { ...state };
        stateCopy.selectedGroup = action.payload;
        return stateCopy;
      }
      case ActionTypes.UNSELECT: {
        const stateCopy = { ...state };
        stateCopy.selectedGroup = undefined;
        return stateCopy;
      }
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(groupsReducer, initialGroupsState);

  useEffect(() => {
    const groupFilter = { kind: 'group' };
    if (state.status === 'initial_load') {
      catalogApi.getEntities({ filter: groupFilter }).then(value => {
        dispatch({
          type: ActionTypes.LOAD_COMPLETED,
          payload: value.items as GroupEntity[],
        });
      });
    }

    if (state.status === 'load') {
      new Promise(resolve => setTimeout(resolve, 2000)).then(() => {
        catalogApi.getEntities({ filter: groupFilter }).then(value => {
          dispatch({
            type: ActionTypes.LOAD_COMPLETED,
            payload: value.items as GroupEntity[],
          });
        });
      });
    }
  }, [catalogApi, state.status]);

  useEffect(() => {
    const createEntity = async () => {
      if (state.status !== 'create') return;
      if (state.newGroup === undefined) return;

      const response = await adminApi.createGroupEntity(state.newGroup);

      if (response.succeeded) {
        await providersApi.triggerProvider('Group');
        toastApi.post({
          title: `Group "${state.newGroup.GroupId}" was created successfully.`,
          status: 'success',
          timeout: 5000,
        });
      } else {
        toastApi.post({
          title: `An error occurred while creating group.`,
          description: response.error,
          status: 'danger',
          timeout: 10000,
        });
      }

      dispatch({ type: ActionTypes.CREATE_COMPLETED });
    };

    createEntity();
  }, [adminApi, toastApi, providersApi, state.status, state.newGroup]);

  useEffect(() => {
    const editEntity = async () => {
      if (state.status !== 'edit') return;
      if (state.newGroup === undefined) return;
      if (state.selectedGroup === undefined) return;

      const response = await adminApi.editGroupEntity(
        state.selectedGroup.spec?.dynamic_uuid as string,
        state.newGroup,
      );

      if (response.succeeded) {
        await providersApi.triggerProvider('Group');
        toastApi.post({
          title: `Group "${state.selectedGroup.metadata.name}" was updated successfully.`,
          status: 'success',
          timeout: 5000,
        });
      } else {
        toastApi.post({
          title: `An error occurred while updating group.`,
          description: response.error,
          status: 'danger',
          timeout: 10000,
        });
      }

      dispatch({ type: ActionTypes.EDIT_COMPLETED });
    };

    editEntity();
  }, [
    adminApi,
    providersApi,
    toastApi,
    state.status,
    state.selectedGroup,
    state.newGroup,
  ]);

  useEffect(() => {
    const deleteEntity = async () => {
      if (state.status !== 'delete') return;
      if (state.selectedGroup === undefined) return;

      const response = await adminApi.deleteGroupEntity(
        state.selectedGroup.spec?.dynamic_uuid as string,
      );

      if (response.succeeded) {
        await providersApi.triggerProvider('Group');
        toastApi.post({
          title: `Group "${state.selectedGroup.metadata.name}" was deleted successfully.`,
          status: 'success',
          timeout: 5000,
        });
      } else {
        toastApi.post({
          title: `An error occurred while deleting group.`,
          description: response.error,
          status: 'danger',
          timeout: 10000,
        });
      }

      dispatch({ type: ActionTypes.DELETE_COMPLETED });
    };

    deleteEntity();
  }, [adminApi, providersApi, toastApi, state.status, state.selectedGroup]);

  const deleteGroup = () => dispatch({ type: ActionTypes.DELETE });
  const createGroup = (group: Group) =>
    dispatch({ type: ActionTypes.CREATE, payload: group });
  const editGroup = (group: Group) =>
    dispatch({ type: ActionTypes.EDIT, payload: group });
  const selectGroup = (group: GroupEntity) =>
    dispatch({ type: ActionTypes.SELECT, payload: group });
  const unselectGroup = () => dispatch({ type: ActionTypes.UNSELECT });

  const context = useMemo(
    () => ({
      groups: state.groups,
      selectedGroup: state.selectedGroup,
      isLoading: state.status !== 'wait',
      deleteGroup,
      createGroup,
      editGroup,
      selectGroup,
      unselectGroup,
    }),
    [state.groups, state.selectedGroup, state.status],
  );

  return (
    <GroupsContext.Provider value={context}>
      {props.children}
    </GroupsContext.Provider>
  );
}
